// src/features/departments/pages/DepartmentsPage/components/GenerateHolidaysDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatDateToLocalString } from '../../../../../i18n/dateUtils';

const defaultRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), 0, 1);
  const end = new Date(today.getFullYear() + 1, 11, 31);
  return { start, end };
};

export default function GenerateHolidaysDialog({ open, department, onClose, onGenerate, loading }) {
  const { t } = useTranslation(['departments', 'common']);
  const [startDate, setStartDate] = useState(() => defaultRange().start);
  const [endDate, setEndDate] = useState(() => defaultRange().end);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      const { start, end } = defaultRange();
      setStartDate(start);
      setEndDate(end);
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      setError(t('departments:generate_holidays.errors.dates_required'));
      return;
    }
    if (endDate < startDate) {
      setError(t('departments:generate_holidays.errors.invalid_range'));
      return;
    }
    setError('');
    const result = await onGenerate(department, {
      start_date: formatDateToLocalString(startDate),
      end_date: formatDateToLocalString(endDate),
    });
    if (result?.success) {
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('departments:generate_holidays.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('departments:generate_holidays.description', { name: department?.name })}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box display="flex" flexDirection="column" gap={2}>
            <DatePicker
              label={t('departments:generate_holidays.start_date')}
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
            <DatePicker
              label={t('departments:generate_holidays.end_date')}
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            {t('departments:actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {t('departments:generate_holidays.generate_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
